import { Localized } from "@fluent/react";
import { LoadingButton } from "@mui/lab";
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Typography,
} from "@mui/material";
import { useState } from "react";
import { deleteDocumentTeamMember } from "../teamMemberApi";
import { useAppDispatch } from "../../../../app/hooks";
import { AppId } from "../../../../app/appTypes";
import { showError, showSuccess } from "../../../notifications/notificationsSlice";
import { DeleteButton } from "../../../../app/components/DeleteButton";
import { CancelButton } from "../../../../app/components/CancelButton";

export interface DeleteTeamMemberDialogProps {
    documentId: AppId;
    teamMemberId: AppId;
    userFullName: string;
    onSuccessfulSave: (teamMemberId: AppId) => void;
    onCancel: () => void;
}

export const DeleteTeamMemberDialog = ({
    documentId,
    teamMemberId,
    userFullName,
    onSuccessfulSave,
    onCancel,
}: DeleteTeamMemberDialogProps) => {
    const dispatch = useAppDispatch();

    const [saving, setSaving] = useState(false);
    const [serverError, setServerError] = useState("");

    const handleOkClick = async () => {
        if (!documentId || !teamMemberId) {
            return;
        }

        setSaving(true);
        setServerError("");

        try {
            const result = await dispatch(
                deleteDocumentTeamMember({ documentId, teamMemberId })
            );

            if (deleteDocumentTeamMember.fulfilled.match(result)) {
                dispatch(showSuccess("deleted"));
                onSuccessfulSave(teamMemberId);
                return;
            }

            let errorMsg = "error";

            if (result.payload?.kind === "http" && result.payload.problem) {
                errorMsg = result.payload.problem.title;
            }

            setServerError(errorMsg);
        } catch {
            dispatch(showError("error"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open fullWidth maxWidth="xs" onClose={saving ? undefined : onCancel}>
            <DialogTitle>
                <Localized id="document-team-member-delete-action">
                    <span>_Usuń członka zespołu</span>
                </Localized>
            </DialogTitle>

            <DialogContent>
                <DialogContentText component="div">

                    <Alert severity="info" variant="outlined" sx={{ mt: 2 }}>
                        <Localized
                            id="document-team-member-delete-confirmation"                    >
                            <span>
                                _Czy na pewno chcesz usunąć tego użytkownika z zespołu
                                dokumentu?
                            </span>
                        </Localized>
                    </Alert>
                    <Box
                        sx={{
                            mt: 2,
                            p: 1.5,
                            borderRadius: 1,
                            bgcolor: "action.hover",
                        }}
                    >
                        <Typography variant="body2" color="text.secondary">
                            <Localized id="user">
                                <span>_Użytkownik</span>
                            </Localized>
                        </Typography>

                        <Typography variant="body1" fontWeight={500}>
                            {userFullName}
                        </Typography>
                    </Box>
                </DialogContentText>

                {serverError && (
                    <Alert
                        sx={{ mt: 2 }}
                        onClose={() => setServerError("")}
                        severity="error"
                    >
                        <Localized id={serverError}>
                            <span>_Server error</span>
                        </Localized>
                    </Alert>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <DeleteButton loading={saving} onClick={handleOkClick} />
                <CancelButton loading={saving} onClick={onCancel} />
            </DialogActions>
        </Dialog>
    );
};